/**
 * BillingNotesEditor Component - Sprint 25
 * 
 * Allows admin to view and edit manual billing notes.
 * 
 * Uses: PATCH /api/billing/tenant/{id}/billing-notes
 */

import React, { useState } from 'react';
import { 
  FileText, 
  Edit2, 
  Save, 
  X,
  Loader2 
} from 'lucide-react';
import { api } from '../../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const BillingNotesEditor = ({ tenantId, initialNotes = '', onUpdate }) => {
  const [notes, setNotes] = useState(initialNotes);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editValue, setEditValue] = useState(initialNotes);

  const handleSave = async () => {
    if (!tenantId) return;
    
    setSaving(true);
    try {
      await api.patch(`/billing/tenant/${tenantId}/billing-notes`, {
        notes: editValue
      });
      setNotes(editValue);
      setIsEditing(false);
      toast.success('Billing notes updated');
      if (onUpdate) onUpdate(editValue);
    } catch (err) {
      console.error('Failed to save notes:', err);
      toast.error('Failed to save billing notes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(notes);
    setIsEditing(false);
  };

  return (
    <Card data-testid="billing-notes-editor">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-600" />
            Billing Notes
          </CardTitle>
          {!isEditing && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              <Edit2 className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-3">
            <textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              placeholder="Add billing notes, invoice references, payment details..."
              className="w-full h-32 p-3 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="billing-notes-input"
            />
            <div className="flex items-center gap-2 justify-end">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleCancel}
                disabled={saving}
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button 
                size="sm"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1" />
                )}
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div 
            className={`p-3 rounded-lg text-sm ${
              notes 
                ? 'bg-slate-50 text-gray-700' 
                : 'bg-gray-50 text-gray-400 italic'
            }`}
          >
            {notes || 'No billing notes. Click Edit to add notes.'}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BillingNotesEditor;
